import { useCallback, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import Img from "./Img";

/** Homepage popup: Registration Week 2027 + financial aid (marketing's graphic, Nov 4). Built as
 *  real text, not the image, so it stays crisp and readable by screen readers. Shows once per
 *  browser session, homepage only (rendered by Home.tsx). The financial aid panel drops off after
 *  its deadline and the popup retires itself after registration week ends: no deploy needed. */
const RETIRE_AFTER = Date.parse("2026-12-04T23:59:59-07:00");
const FIN_AID_CLOSES = Date.parse("2026-11-13T23:59:59-07:00");
const DISMISS_KEY = "reg-week-popup-2027";
const TRIPS_URL = "https://destinations.hxp.org/";
const FIN_AID_URL = "https://hxp.org/hxp-financial-aid-program/";

const oswald = { fontFamily: "'Oswald', system-ui, sans-serif" };

const FinAidPopup = () => {
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const lastFocus = useRef<Element | null>(null);

  useEffect(() => {
    if (Date.now() > RETIRE_AFTER) return;
    try {
      if (sessionStorage.getItem(DISMISS_KEY)) return;
    } catch {
      /* storage blocked: still show it once for this page view */
    }
    const t = setTimeout(() => {
      lastFocus.current = document.activeElement;
      setOpen(true);
    }, 1200);
    return () => clearTimeout(t);
  }, []);

  const dismiss = useCallback(() => {
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setOpen(false);
    (lastFocus.current as HTMLElement | null)?.focus?.();
  }, []);

  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        dismiss();
        return;
      }
      // Keep Tab focus inside the dialog.
      if (e.key !== "Tab" || !dialogRef.current) return;
      const els = dialogRef.current.querySelectorAll<HTMLElement>("a[href], button");
      if (!els.length) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, dismiss]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300"
      onClick={dismiss}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="finaid-popup-title"
        className="relative max-h-[92vh] w-full max-w-md overflow-y-auto rounded-3xl bg-[#efe8de] text-center shadow-2xl motion-safe:animate-in motion-safe:zoom-in-95 motion-safe:duration-300"
        onClick={(e) => e.stopPropagation()}
        style={oswald}
      >
        <button
          ref={closeRef}
          type="button"
          onClick={dismiss}
          aria-label="Close"
          className="absolute right-3 top-3 z-10 rounded-full bg-white/90 p-2 text-foreground transition-colors hover:bg-white"
        >
          <X size={20} />
        </button>

        <div className="relative overflow-hidden px-6 pb-6 pt-16">
          <Img
            src="/images/audience/vest-crew.jpg"
            alt=""
            sizes="(min-width: 480px) 448px, 100vw"
            loading="eager"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-[#3a322f]/65" />
          <h2 id="finaid-popup-title" className="relative text-5xl font-bold uppercase leading-none text-white md:text-6xl" style={oswald}>
            Registration
            <br />
            Week <span className="text-[#e9bab4]">2027</span>
          </h2>
        </div>

        <div className="px-6 pb-6 pt-5 md:px-8 md:pb-8">
          <p className="text-4xl font-bold uppercase text-[#b94737] md:text-5xl">Nov 30 – Dec 4</p>
          <p className="mt-2 text-sm uppercase tracking-[0.08em] text-[#383632]">Trips live now</p>

          <a
            href={TRIPS_URL}
            target="_self"
            className="mt-5 flex items-center justify-center rounded-xl bg-[#b94737] px-6 py-3.5 text-base font-semibold uppercase tracking-[0.1em] text-white transition-colors hover:bg-[#8f3426]"
          >
            2027 Trip Locations
          </a>

          {Date.now() <= FIN_AID_CLOSES && (
            <div className="mt-5 rounded-2xl bg-[#8a6e2f] p-5">
              <p className="text-2xl font-semibold uppercase leading-tight tracking-[0.03em] text-[#f0e6aa]">
                Financial aid applications open now!
              </p>
              <p className="mt-2 text-xs uppercase tracking-[0.08em] text-white">Closes November 13</p>
              <a
                href={FIN_AID_URL}
                target="_self"
                className="mt-4 flex items-center justify-center rounded-xl bg-[#f0e6aa] px-4 py-3.5 text-base font-semibold uppercase tracking-[0.1em] text-[#735b25] transition-colors hover:bg-white"
              >
                Financial Aid Application
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinAidPopup;
